import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const Section = styled.section`
  margin: 100px 0;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled.h2`
  color: var(--lightest-slate);
`;

const Subtitle = styled.p`
  color: var(--slate);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const Card = styled.article`
  background: var(--light-navy);
  border: 1px solid var(--lightest-navy);
  border-radius: var(--border-radius);
  padding: 16px;
  display: flex;
  flex-direction: column;
  transition: var(--transition);
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--green);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);

    &:before {
      transform: scaleX(1);
    }
  }
`;

const Thumbnail = styled.div`
  width: 100%;
  height: 180px;
  background: var(--navy);
  border-radius: var(--border-radius);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledGatsbyImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledH1 = styled.h1`
  font-size: 3rem;
  color: var(--green);
  margin: 0;
  line-height: 1;
`;
const Name = styled.h3`
  color: var(--lightest-slate);
  margin: 8px 0 6px;
  font-size: var(--fz-xl);
`;

const Desc = styled.p`
  color: var(--slate);
  font-size: var(--fz-md);
  line-height: 1.5;
  margin: 0;
`;

const FeatureList = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  color: var(--slate);

  li {
    margin-bottom: 6px;
    font-size: var(--fz-sm);
  }
`;

const LinkBigButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 80px;
`;

const BigButton = styled.a`
  ${({ theme }) => theme.mixins.bigButton};
`;

const ServicesHome = () => {
  const data = useStaticQuery(graphql`
    query ServicesHomeQuery {
      services: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/services/" } }
        sort: { fields: [frontmatter___order], order: ASC }
        limit: 6
      ) {
        edges {
          node {
            id
            frontmatter {
              title
              description
              features
              showInServices
              cover {
                childImageSharp {
                  gatsbyImageData(
                    width: 600
                    height: 350
                    placeholder: BLURRED
                    formats: [AUTO, WEBP]
                  )
                }
              }
            }
          }
        }
      }
    }
  `);

  const items = (data.services?.edges || [])
    .map(({ node }) => node)
    .filter(n => n.frontmatter?.showInServices);

  if (!items.length) {
    return null;
  }

  return (
    <Section id="services">
      <Header>
        <Title className="numbered-heading">Services</Title>
        <Subtitle>End‑to‑end solutions tailored to your needs.</Subtitle>
      </Header>

      <Grid>
        {items.map(({ id, frontmatter }) => {
          const { title, description, features, cover } = frontmatter;
          const image = cover ? getImage(cover) : null;

          return (
            <Card key={id}>
              <Thumbnail>
                {image ? (
                  <StyledGatsbyImage image={image} alt={title} />
                ) : (
                  <StyledH1>{title?.charAt(0) || 'S'}</StyledH1>
                )}
              </Thumbnail>
              <Name>{frontmatter.title}</Name>
              {frontmatter.description && <Desc>{frontmatter.description}</Desc>}
              {Array.isArray(frontmatter.features) && frontmatter.features.length > 0 && (
                <FeatureList>
                  {frontmatter.features.slice(0, 5).map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </FeatureList>
              )}
            </Card>
          );
        })}
      </Grid>

      <LinkBigButtonWrapper>
        <BigButton href="/services">View All Services</BigButton>
      </LinkBigButtonWrapper>
    </Section>
  );
};

export default ServicesHome;
